import clsx from "clsx";
import {
  ReactElement,
  ReactNode,
  isValidElement,
  memo,
  FC,
  Children,
  JSXElementConstructor,
} from "react";
import { MdArrowRight } from "react-icons/md";
import { ICON_SIZES } from "src/helpers/constants";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

export enum ELinkType {
  INTERNAL = "INTERNAL",
  EXTERNAL = "EXTERNAL",
}

export interface ITextListItem {
  text: string;
  link?: {
    type: ELinkType;
    to: string;
  };
}

interface ITextListProps {
  items: ITextListItem[];
  children?: ReactNode;
}

const TextList: FC<ITextListProps> & {
  Top: FC<{ children: ReactNode }>;
  Bottom: FC<{ children: ReactNode }>;
} = ({ items, children }) => {
  const navigate = useNavigate();
  if (!items?.length) return null;

  let top: ReactNode = null;
  let bottom: ReactNode = null;

  const isComponentWithDisplayName = (
    element: any,
    displayName: string
  ): boolean => {
    return (
      isValidElement(element) &&
      typeof element.type !== "string" &&
      (element.type as { displayName?: string }).displayName === displayName
    );
  };

  Children.forEach(children, (child) => {
    if (isComponentWithDisplayName(child, "TextList.Top")) {
      top = child;
    } else if (isComponentWithDisplayName(child, "TextList.Bottom")) {
      bottom = child;
    }
  });

  return (
    <div className={clsx(["flex", "flex-col", "gap-4"])}>
      {top}
      <ul
        className={clsx([
          "flex",
          "w-full",
          "flex-col",
          "list-disc",
          "list-inside",
          "ml-2",
          "text-sm",
          "md:text-base",
        ])}
      >
        {items.map((item, key) => (
          <motion.li
            key={key}
            className={clsx([
              "inline-flex",
              "flex-row",
              "gap-1",
              "items-center",
            ])}
            initial={{
              opacity: 0,
              x: key % 2 === 0 ? -100 : 100,
            }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeOut",
              delay: key * 0.1,
            }}
          >
            <MdArrowRight
              size={ICON_SIZES.MD}
              className="dark:text-purple-400 text-blue-400"
            />
            <div
              onClick={() => {
                if (item?.link && item?.link?.type === ELinkType.INTERNAL) {
                  navigate(`${item?.link?.to}`);
                }
              }}
              className={clsx([
                "inline",
                "relative",
                {
                  "after-animated-underline": item?.link,
                },
              ])}
            >
              {item.text}
            </div>
          </motion.li>
        ))}
      </ul>
      {bottom}
    </div>
  );
};

const Top: FC<{ children: ReactNode }> = ({ children }) => <>{children}</>;
Top.displayName = "TextList.Top";

const Bottom: FC<{ children: ReactNode }> = ({ children }) => <>{children}</>;
Bottom.displayName = "TextList.Bottom";

TextList.Top = Top;
TextList.Bottom = Bottom;

export default TextList;
